import { useRemaining } from '../app/clock';
import type { GameState } from '../app/state';
import { playerName } from '../app/state';
import { Countdown } from '../ui/Countdown';
import { Standings } from '../ui/Standings';
import s from './screens.module.scss';

/**
 * La resolution.
 *
 * C'est le seul ecran ou la reponse existe cote client — le serveur ne
 * l'envoie qu'ici, une fois la manche close. Un test du serveur verifie
 * qu'aucun message anterieur ne la transporte ; ce client n'a donc aucun
 * moyen de la montrer plus tot, meme si on le lui demandait.
 *
 * Les `notes` sont le journal des pouvoirs, redige par le serveur dans un
 * ordre fige. C'est ce qui rend une partie incontestable entre amis : on lit
 * la meme ligne, dans le meme ordre, chez tout le monde.
 */
export interface ResultScreenProps {
  state: GameState;
}

export function ResultScreen({ state }: ResultScreenProps) {
  const result = state.result;
  const remaining = useRemaining(state.phaseEndsAt);

  if (!result) return null;

  return (
    <main className={s.result}>
      <header className={s.resultHeader}>
        <Countdown remainingMs={remaining} totalMs={state.phaseTotalMs} label="Manche suivante" />
      </header>

      <section className={s.reveal}>
        <span className={s.revealLabel}>La reponse</span>
        {/* Toujours l'image nette, jamais celle (pixelisee) qu'on vient de
            jouer : le recap doit se lire d'un coup d'oeil, sans mosaique. */}
        {result.imageUrl ? (
          <img className={s.revealImage} src={result.imageUrl} alt="" draggable={false} />
        ) : null}
        <h1 className={s.revealAnswer}>{result.answer}</h1>
        {result.subtitle ? <p className={s.revealSubtitle}>{result.subtitle}</p> : null}
      </section>

      <div className={s.resultColumns}>
        <section className={s.resultLog}>
          <h2 className={s.panelTitle}>Ce qui s'est passe</h2>

          {result.notes.length > 0 ? (
            <ul className={s.noteList}>
              {result.notes.map((note, index) => (
                <li
                  key={index}
                  className={s.note}
                  style={{ animationDelay: `calc(var(--dur-fast) * ${index})` }}
                >
                  {note}
                </li>
              ))}
            </ul>
          ) : (
            <p className={s.resultNote}>Aucun pouvoir n'est intervenu.</p>
          )}

          {result.bailedOut.length > 0 ? (
            <p className={s.bailout}>
              Renfloue : {result.bailedOut.map((id) => playerName(state, id)).join(', ')}
            </p>
          ) : null}
        </section>

        {/* Le classement reste a droite sur les trois ecrans de manche (mise,
            reponse, resultat) — c'est la seule chose qui ne doit jamais
            sauter d'un cote a l'autre, sous peine de perdre le fil des scores. */}
        <Standings state={state} standings={result.standings} title="Classement" />
      </div>
    </main>
  );
}
